import { useEffect, useState, useCallback, useRef } from 'react';
import Modal from '../../components/common/Modal';
import { useParams, useNavigate } from 'react-router-dom';
import OrderDetail from '../../components/orders/OrderDetail';
import Alert from '../../components/common/Alert';
import Loading from '../../components/common/Loading';
import {
  getOrderAsEmployee,
  updateOrderStatus,
  updateOrderNotes,
  updateOrderDetails,
  createInvoice,
  sendInvoice,
  resendInvoice,
  markPaidOutsideStripe,
  voidInvoice,
  getInvoiceLinks,
  setDiscount,
} from '../../services/orders';
import { getAddresses } from '../../services/customers';
import { getSessions } from '../../services/sessions';
import { getProducts } from '../../services/products';
import { logError } from '../../utils/errorHandler';
import { formatCurrency, formatDate } from '../../utils/formatters';
import './EmployeeListPage.css';
import './OrderDetail.css';

const PAYMENT_STATUS_LABELS = {
  open: 'Open',
  paid: 'Paid',
  overdue: 'Overdue',
  payment_failed: 'Payment Failed',
  voided: 'Voided',
};

// Statuses where we warn the employee before editing
const WARN_STATUSES = new Set([
  'in_progress', 'fulfilled', 'out_for_delivery', 'delivered',
  'ready_for_pickup', 'picked_up',
]);

const EmployeeOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ── Order data ───────────────────────────────────────────
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  // ── Notes draft (local buffer — saved explicitly) ────────
  const [notesDraft, setNotesDraft]     = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [notesSaved, setNotesSaved]     = useState(false);
  const notesSavedTimerRef = useRef(null);

  // ── Alert ────────────────────────────────────────────────
  const [alert, setAlert] = useState(null);

  // ── Saving state ─────────────────────────────────────────
  const [isUpdating, setIsUpdating] = useState(false);

  // ── Invoice actions ──────────────────────────────────────
  const [isInvoicing, setIsInvoicing] = useState(false);
  const [invoiceLinks, setInvoiceLinks] = useState(null);
  const [isFetchingLinks, setIsFetchingLinks] = useState(false);
  const [applyCredit, setApplyCredit] = useState(true);
  const [showVoidConfirm, setShowVoidConfirm] = useState(false);

  // ── Discount ─────────────────────────────────────────────
  const [discountInput, setDiscountInput] = useState('');
  const [isSavingDiscount, setIsSavingDiscount] = useState(false);

  // ── Purchase order ───────────────────────────────────────
  const [poNumber, setPoNumber] = useState('');
  const [poNotes, setPoNotes] = useState('');
  const [isSavingPO, setIsSavingPO] = useState(false);

  // ── Edit mode ────────────────────────────────────────────
  const [editMode, setEditMode] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);

  // ── Edit mode data (addresses, sessions, products) ───────
  const [addresses, setAddresses] = useState([]);
  const [addressesLoading, setAddressesLoading] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const editDataFetchedRef = useRef(false);

  // ── Fetch order on mount ─────────────────────────────────
  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        setFetchError(null);
        const orderData = await getOrderAsEmployee(parseInt(id, 10));
        setOrder(orderData);
        setNotesDraft(orderData.internal_notes || '');
        setDiscountInput(orderData.discount_cents > 0 ? (orderData.discount_cents / 100).toFixed(2) : '');
        setPoNumber(orderData.po_number || '');
        setPoNotes(orderData.po_notes || '');
      } catch (err) {
        logError('Load employee order detail', err);
        setFetchError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrder();
  }, [id]);

  // ── Cleanup timers on unmount; reset edit data ref so a
  //    full page remount (e.g. navigating away and back) triggers
  //    a fresh fetch rather than showing potentially stale data.
  useEffect(() => {
    return () => {
      clearTimeout(notesSavedTimerRef.current);
      editDataFetchedRef.current = false;
    };
  }, []);

  // ── Fetch edit mode data (lazy, once per mount) ─────────
  //    Data is fetched once when the employee first enters edit mode and
  //    cached for the lifetime of this page instance. The ref is reset on
  //    unmount (see cleanup effect above) so navigating away and back will
  //    trigger a fresh fetch, keeping data reasonably up to date.
  const fetchEditData = useCallback(async (companyId) => {
    if (!companyId) return;
    if (editDataFetchedRef.current) return;
    editDataFetchedRef.current = true;

    setAddressesLoading(true);
    setSessionsLoading(true);
    setProductsLoading(true);

    getAddresses(companyId)
      .then((data) => {
        const all = Array.isArray(data) ? data : (data.results ?? []);
        setAddresses(all.filter((a) => a.address_type === 'shipping'));
      })
      .catch(() => setAddresses([]))
      .finally(() => setAddressesLoading(false));

    getSessions({ employee: true })
      .then((data) => setSessions(Array.isArray(data) ? data : (data.results ?? [])))
      .catch(() => setSessions([]))
      .finally(() => setSessionsLoading(false));

    getProducts()
      .then((data) => setProducts(Array.isArray(data) ? data : (data.results ?? [])))
      .catch(() => setProducts([]))
      .finally(() => setProductsLoading(false));
  }, []);

  const handleEnterEditMode = useCallback(() => {
    if (order?.company) {
      const companyId = typeof order.company === 'object' ? order.company.id : order.company;
      fetchEditData(companyId);
    }
    setEditMode(true);
  }, [order, fetchEditData]);

  const handleCancelEdit = useCallback(() => {
    setEditMode(false);
  }, []);

  // ── Save order edits ─────────────────────────────────────
  const handleSaveEdit = useCallback(async (editData) => {
    if (!order) return;
    try {
      setIsSavingEdit(true);
      const updated = await updateOrderDetails(order.id, editData);
      setOrder(updated);
      setNotesDraft(updated.internal_notes || '');
      setEditMode(false);
      setAlert({ type: 'success', message: 'Order updated successfully.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Failed to save order.' });
    } finally {
      setIsSavingEdit(false);
    }
  }, [order]);

  // ── Status update (fires immediately on select change) ───
  const handleStatusUpdate = useCallback(async (newStatus) => {
    if (!order) return;
    if (newStatus === order.status) return;
    const prevStatus = order.status;
    setOrder((prev) => ({ ...prev, status: newStatus }));
    try {
      setIsUpdating(true);
      const updated = await updateOrderStatus(order.id, newStatus);
      setOrder((prev) => ({ ...prev, status: updated?.status ?? newStatus }));
    } catch (err) {
      setOrder((prev) => ({ ...prev, status: prevStatus }));
      setAlert({ type: 'error', message: err.message || 'Failed to update status.' });
    } finally {
      setIsUpdating(false);
    }
  }, [order]);

  // ── Notes draft handler (local only) ────────────────────
  const handleNotesDraftChange = useCallback((value) => {
    setNotesDraft(value);
    setNotesSaved(false);
  }, []);

  // ── Save notes (explicit button) ─────────────────────────
  const handleSaveNotes = useCallback(async () => {
    if (!order) return;
    try {
      setIsSavingNotes(true);
      const updated = await updateOrderNotes(order.id, notesDraft);
      setOrder((prev) => ({ ...prev, internal_notes: updated?.internal_notes ?? notesDraft }));
      setNotesSaved(true);
      notesSavedTimerRef.current = setTimeout(() => setNotesSaved(false), 2500);
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Failed to save notes.' });
    } finally {
      setIsSavingNotes(false);
    }
  }, [order, notesDraft]);

  // ── Invoice action handlers ──────────────────────────────
  const handleCreateInvoice = useCallback(async () => {
    if (!order) return;
    try {
      setIsInvoicing(true);
      const updated = await createInvoice(order.id, applyCredit);
      setOrder((prev) => ({ ...prev, ...updated }));
      setAlert({ type: 'success', message: 'Invoice created.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Failed to create invoice.' });
    } finally {
      setIsInvoicing(false);
    }
  }, [order, applyCredit]);

  const handleSendInvoice = useCallback(async () => {
    if (!order) return;
    try {
      setIsInvoicing(true);
      const updated = await sendInvoice(order.id);
      setOrder((prev) => ({ ...prev, ...updated }));
      setAlert({ type: 'success', message: 'Invoice sent to customer.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Failed to send invoice.' });
    } finally {
      setIsInvoicing(false);
    }
  }, [order]);

  const handleResendInvoice = useCallback(async () => {
    if (!order) return;
    try {
      setIsInvoicing(true);
      await resendInvoice(order.id);
      setAlert({ type: 'success', message: 'Invoice resent to customer.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Failed to resend invoice.' });
    } finally {
      setIsInvoicing(false);
    }
  }, [order]);

  const handleVoidInvoice = useCallback(() => {
    if (!order) return;
    setShowVoidConfirm(true);
  }, [order]);

  const confirmVoidInvoice = useCallback(async () => {
    setShowVoidConfirm(false);
    try {
      setIsInvoicing(true);
      setInvoiceLinks(null);
      const updated = await voidInvoice(order.id);
      setOrder((prev) => ({ ...prev, ...updated }));
      setAlert({ type: 'success', message: 'Invoice voided. You can now create a new invoice.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Failed to void invoice.' });
    } finally {
      setIsInvoicing(false);
    }
  }, [order]);

  const handleMarkPaidOutsideStripe = useCallback(async () => {
    if (!order) return;
    try {
      setIsInvoicing(true);
      const updated = await markPaidOutsideStripe(order.id);
      setOrder((prev) => ({ ...prev, ...updated }));
      setAlert({ type: 'success', message: 'Invoice marked as paid.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Failed to mark invoice paid.' });
    } finally {
      setIsInvoicing(false);
    }
  }, [order]);

  const handleSaveDiscount = useCallback(async () => {
    if (!order) return;
    const dollars = parseFloat(discountInput) || 0;
    const cents = Math.round(dollars * 100);
    try {
      setIsSavingDiscount(true);
      const updated = await setDiscount(order.id, cents);
      setOrder((prev) => ({ ...prev, ...updated }));
      setDiscountInput(cents > 0 ? (cents / 100).toFixed(2) : '');
      setAlert({ type: 'success', message: 'Discount saved.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Failed to save discount.' });
    } finally {
      setIsSavingDiscount(false);
    }
  }, [order, discountInput]);

  const handleSavePO = useCallback(async () => {
    if (!order) return;
    try {
      setIsSavingPO(true);
      const updated = await updateOrderDetails(order.id, {
        po_number: poNumber.trim(),
        po_notes: poNotes.trim(),
      });
      setOrder((prev) => ({ ...prev, ...updated }));
      setAlert({ type: 'success', message: 'PO details saved.' });
    } catch (err) {
      setAlert({ type: 'error', message: err.message || 'Failed to save PO details.' });
    } finally {
      setIsSavingPO(false);
    }
  }, [order, poNumber, poNotes]);

  const poDirty =
    order &&
    (poNumber.trim() !== (order.po_number || '') ||
      poNotes.trim() !== (order.po_notes || ''));

  const notesDirty = order && notesDraft !== (order.internal_notes || '');

  // ── Loading state ────────────────────────────────────────
  if (loading) {
    return (
      <div className="emp-order-detail-page emp-order-detail-page--loading">
        <Loading />
        <p>Loading order…</p>
      </div>
    );
  }

  // ── Error state ──────────────────────────────────────────
  if (fetchError) {
    return (
      <div className="emp-order-detail-page emp-order-detail-page--error">
        <Alert type="error">
          {fetchError.message || 'Failed to load order. Please try again.'}
        </Alert>
        <button
          className="emp-order-detail-page__back"
          onClick={() => navigate('/employee/orders')}
        >
          ← Back to Orders
        </button>
      </div>
    );
  }

  const showEditWarning = editMode && order && WARN_STATUSES.has(order.status);

  // ── Render ───────────────────────────────────────────────
  return (
    <div className="employee-page emp-order-detail-page">

      {/* ── Alert ─────────────────────────────────────────── */}
      {alert && (
        <Alert
          type={alert.type}
          message={alert.message}
          dismissible
          onDismiss={() => setAlert(null)}
          className="emp-list__alert"
        />
      )}

      {/* ── Header ────────────────────────────────────────── */}
      <div className="employee-page__header emp-order-detail-page__header">
        <div>
          <p className="employee-page__eyebrow">Employee Portal / Orders</p>
          <h1 className="employee-page__title">{order?.order_number}</h1>
        </div>
        <div className="emp-order-detail-page__header-actions">
          {!editMode ? (
            <>
              <button
                type="button"
                className="emp-order-detail-page__edit-btn"
                onClick={handleEnterEditMode}
                aria-label="Edit order"
              >
                ✏️ Edit Order
              </button>
              <button
                type="button"
                className="emp-order-detail-page__print-btn"
                onClick={() => window.open(`/print-packlist?id=${order.id}`, '_blank')}
                aria-label="Print packlist"
              >
                🖨 Print Packlist
              </button>
            </>
          ) : (
            <button
              type="button"
              className="emp-order-detail-page__cancel-edit-btn"
              onClick={handleCancelEdit}
              aria-label="Cancel editing"
            >
              ✕ Cancel Edit
            </button>
          )}
          <button
            type="button"
            className="emp-order-detail-page__back"
            onClick={() => navigate('/employee/orders')}
            aria-label="Back to orders list"
          >
            ← All Orders
          </button>
        </div>
      </div>

      {/* ── Edit-in-progress warning (below header) ────────── */}
      {showEditWarning && (
        <div className="emp-order-detail-page__edit-warning">
          <span className="emp-order-detail-page__edit-warning-icon">⚠️</span>
          <span>
            <strong>Heads up:</strong> This order is already <strong>{order.status.replace(/_/g, ' ')}</strong>. Any changes you save will update a live order.
          </span>
        </div>
      )}

      {/* ── Merge banners ─────────────────────────────────── */}
      {order?.status === 'merged' && order?.merged_into && (
        <div className="emp-order-detail-page__merge-banner">
          This order was merged into{' '}
          <button
            type="button"
            className="emp-order-detail-page__merge-link"
            onClick={() => navigate(`/employee/orders/${order.merged_into}`)}
          >
            {order.merged_into_number}
          </button>
          . Invoice the consolidated order instead.
        </div>
      )}
      {order?.merged_from_numbers?.length > 0 && (
        <div className="emp-order-detail-page__merge-banner emp-order-detail-page__merge-banner--target">
          Consolidated from {order.merged_from_numbers.join(', ')}.
        </div>
      )}

      {/* ── Order detail ──────────────────────────────────── */}
      <OrderDetail
        order={{ ...order, internal_notes: notesDraft }}
        isEmployee
        editMode={editMode}
        onStatusUpdate={handleStatusUpdate}
        onNotesUpdate={handleNotesDraftChange}
        onSaveEdit={handleSaveEdit}
        onCancelEdit={handleCancelEdit}
        isSavingEdit={isSavingEdit}
        addresses={addresses}
        addressesLoading={addressesLoading}
        sessions={sessions}
        sessionsLoading={sessionsLoading}
        products={products}
        productsLoading={productsLoading}
      />

      {/* ── Invoice panel (hidden in edit mode and for merged sources) ── */}
      {!editMode && order && order.status !== 'merged' && (
        <div className="emp-order-detail-page__invoice-panel">
          <h2 className="emp-order-detail-page__invoice-title">Invoicing</h2>

          {/* ── Billing info ──────────────────────────────────── */}
          {order.company_detail && (
            <div className="emp-order-detail-page__billing-info">
              {order.company_detail.billing_contacts?.length > 0 ? (
                order.company_detail.billing_contacts.map((c) => (
                  <div key={c.id} className="emp-order-detail-page__billing-row">
                    <span className="emp-order-detail-page__billing-label">
                      {c.is_primary ? 'Billing (Primary)' : 'Billing (CC)'}
                    </span>
                    <span className="emp-order-detail-page__billing-value">
                      {c.name} &lt;{c.email}&gt;
                    </span>
                  </div>
                ))
              ) : (
                <>
                  {order.company_detail.billing_contact_name && (
                    <div className="emp-order-detail-page__billing-row">
                      <span className="emp-order-detail-page__billing-label">Contact</span>
                      <span className="emp-order-detail-page__billing-value">{order.company_detail.billing_contact_name}</span>
                    </div>
                  )}
                  {order.company_detail.billing_email && (
                    <div className="emp-order-detail-page__billing-row">
                      <span className="emp-order-detail-page__billing-label">Email</span>
                      <span className="emp-order-detail-page__billing-value">{order.company_detail.billing_email}</span>
                    </div>
                  )}
                </>
              )}
              {order.company_detail.billing_address && (
                <div className="emp-order-detail-page__billing-row">
                  <span className="emp-order-detail-page__billing-label">Address</span>
                  <span className="emp-order-detail-page__billing-value">
                    {order.company_detail.billing_address.street_address}
                    {order.company_detail.billing_address.street_address_2 && `, ${order.company_detail.billing_address.street_address_2}`}
                    {', '}{order.company_detail.billing_address.city}, {order.company_detail.billing_address.state} {order.company_detail.billing_address.zip_code}
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Badge + links — shown whenever an invoice exists */}
          {order.stripe_invoice_id && (
            <div className="emp-order-detail-page__invoice-meta">
              <span className={`emp-order-detail-page__payment-badge emp-order-detail-page__payment-badge--${order.payment_status}`}>
                {PAYMENT_STATUS_LABELS[order.payment_status] || order.payment_status}
              </span>
              {order.payment_date && (
                <span className="emp-order-detail-page__payment-date">
                  Paid {formatDate(order.payment_date)}
                </span>
              )}
              {order.po_number && (
                <span className="emp-order-detail-page__invoice-po">
                  PO: {order.po_number}
                  {order.po_notes && ` — ${order.po_notes}`}
                </span>
              )}
              {invoiceLinks ? (
                <>
                  {invoiceLinks.hosted_invoice_url && (
                    <a
                      href={invoiceLinks.hosted_invoice_url}
                      target="_blank"
                      rel="noreferrer"
                      className="emp-order-detail-page__invoice-link"
                    >
                      View Invoice ↗
                    </a>
                  )}
                  {invoiceLinks.invoice_pdf && (
                    <a
                      href={invoiceLinks.invoice_pdf}
                      target="_blank"
                      rel="noreferrer"
                      className="emp-order-detail-page__invoice-link"
                    >
                      Download PDF ↗
                    </a>
                  )}
                </>
              ) : (order.invoice_sent_at || order.payment_status === 'paid' || order.payment_status === 'voided') ? (
                <button
                  type="button"
                  className="emp-order-detail-page__invoice-link-btn"
                  disabled={isFetchingLinks}
                  onClick={async () => {
                    setIsFetchingLinks(true);
                    try {
                      const links = await getInvoiceLinks(order.id);
                      setInvoiceLinks(links);
                    } catch {
                      setAlert({ type: 'error', message: 'Could not fetch invoice links.' });
                    } finally {
                      setIsFetchingLinks(false);
                    }
                  }}
                >
                  {isFetchingLinks ? 'Loading…' : 'Get Links'}
                </button>
              ) : null}
            </div>
          )}

          {/* Send / Resend / Void actions — open invoices only */}
          {order.stripe_invoice_id && order.payment_status !== 'paid' && order.payment_status !== 'voided' && (
            <div className="emp-order-detail-page__invoice-actions">
              {!order.invoice_sent_at ? (
                <button
                  type="button"
                  className="emp-order-detail-page__invoice-btn"
                  onClick={handleSendInvoice}
                  disabled={isInvoicing}
                >
                  {isInvoicing ? 'Sending…' : 'Send Invoice'}
                </button>
              ) : (
                <>
                  <span className="emp-order-detail-page__invoice-sent-indicator">
                    ✓ Sent
                  </span>
                  <button
                    type="button"
                    className="emp-order-detail-page__invoice-btn"
                    onClick={handleResendInvoice}
                    disabled={isInvoicing}
                  >
                    {isInvoicing ? 'Sending…' : 'Resend Invoice'}
                  </button>
                </>
              )}
              <button
                type="button"
                className="emp-order-detail-page__invoice-btn emp-order-detail-page__invoice-btn--secondary"
                onClick={handleMarkPaidOutsideStripe}
                disabled={isInvoicing}
              >
                Mark Paid (Check)
              </button>
              <button
                type="button"
                className="emp-order-detail-page__invoice-btn emp-order-detail-page__invoice-btn--secondary"
                onClick={handleVoidInvoice}
                disabled={isInvoicing}
              >
                {isInvoicing ? 'Working…' : 'Void Invoice'}
              </button>
            </div>
          )}

          {/* Create invoice — no invoice yet, or previous invoice was voided */}
          {(!order.stripe_invoice_id || order.payment_status === 'voided') && (
            <div className="emp-order-detail-page__invoice-actions">
              {order.company_detail?.credit_balance_cents > 0 && (
                <label className="emp-order-detail-page__credit-option">
                  <input
                    type="checkbox"
                    checked={applyCredit}
                    onChange={(e) => setApplyCredit(e.target.checked)}
                  />
                  Apply {formatCurrency(order.company_detail.credit_balance_cents / 100)} account credit to this invoice
                </label>
              )}
              <div className="emp-order-detail-page__discount-section">
                <div className="emp-order-detail-page__discount-row">
                  <label className="emp-order-detail-page__discount-label" htmlFor="discount-input">
                    Discount
                  </label>
                  <span className="emp-order-detail-page__discount-prefix">$</span>
                  <input
                    id="discount-input"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="0.00"
                    className="emp-order-detail-page__discount-input"
                    value={discountInput}
                    onChange={(e) => setDiscountInput(e.target.value)}
                  />
                  <button
                    type="button"
                    className="emp-order-detail-page__discount-apply-btn"
                    onClick={handleSaveDiscount}
                    disabled={isSavingDiscount}
                  >
                    {isSavingDiscount ? 'Saving…' : 'Apply Discount'}
                  </button>
                </div>
                {order.discount_cents > 0 && (
                  <div className="emp-order-detail-page__discount-applied">
                    ✓ {formatCurrency(order.discount_cents / 100)} discount applied to this order
                  </div>
                )}
              </div>
              <div className="emp-order-detail-page__po-section">
                <div className="emp-order-detail-page__po-row">
                  <label className="emp-order-detail-page__po-label" htmlFor="po-number-input">
                    PO Number
                  </label>
                  <input
                    id="po-number-input"
                    type="text"
                    maxLength={100}
                    placeholder="e.g. PO-48213"
                    className="emp-order-detail-page__po-input"
                    value={poNumber}
                    onChange={(e) => setPoNumber(e.target.value)}
                  />
                </div>
                <div className="emp-order-detail-page__po-row">
                  <label className="emp-order-detail-page__po-label" htmlFor="po-notes-input">
                    PO Notes
                  </label>
                  <textarea
                    id="po-notes-input"
                    rows={2}
                    maxLength={1000}
                    placeholder="Optional details shown on the invoice (terms, department, etc.)"
                    className="emp-order-detail-page__po-notes"
                    value={poNotes}
                    onChange={(e) => setPoNotes(e.target.value)}
                  />
                </div>
                <div className="emp-order-detail-page__po-actions">
                  <button
                    type="button"
                    className="emp-order-detail-page__po-apply-btn"
                    onClick={handleSavePO}
                    disabled={isSavingPO || !poDirty}
                  >
                    {isSavingPO ? 'Saving…' : 'Save PO Details'}
                  </button>
                  {!poDirty && (order.po_number || order.po_notes) && (
                    <span className="emp-order-detail-page__po-saved">✓ Saved</span>
                  )}
                </div>
                <p className="emp-order-detail-page__po-hint">
                  Set the PO before creating the invoice so it appears on the invoice the customer receives.
                </p>
              </div>
              <div className="emp-order-detail-page__invoice-btn-row">
                <button
                  type="button"
                  className="emp-order-detail-page__invoice-btn"
                  onClick={handleCreateInvoice}
                  disabled={isInvoicing}
                >
                  {isInvoicing ? 'Creating…' : 'Create Invoice'}
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Notes save toolbar (hidden in edit mode) ────────── */}
      {!editMode && (
        <div className={`emp-order-detail-page__notes-toolbar${notesDirty || notesSaved ? ' emp-order-detail-page__notes-toolbar--visible' : ''}`}>
          {notesSaved ? (
            <span className="emp-order-detail-page__notes-saved">✓ Notes saved</span>
          ) : (
            <>
              <span className="emp-order-detail-page__notes-hint">Unsaved changes</span>
              <button
                type="button"
                className="emp-order-detail-page__notes-btn"
                onClick={handleSaveNotes}
                disabled={isSavingNotes || isUpdating}
              >
                {isSavingNotes ? 'Saving…' : 'Save Notes'}
              </button>
            </>
          )}
        </div>
      )}

      <Modal
        isOpen={showVoidConfirm}
        onClose={() => setShowVoidConfirm(false)}
        title="Void Invoice"
        size="small"
      >
        <p>This will void the Stripe invoice. If it was already sent, the customer will receive a void notification. You can then create a new invoice manually.</p>
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '1.25rem' }}>
          <button
            type="button"
            className="emp-order-detail-page__invoice-btn emp-order-detail-page__invoice-btn--secondary"
            onClick={() => setShowVoidConfirm(false)}
            style={{ color: '#ccc', borderColor: '#555' }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="emp-order-detail-page__invoice-btn emp-order-detail-page__invoice-btn--danger"
            onClick={confirmVoidInvoice}
          >
            Void Invoice
          </button>
        </div>
      </Modal>

    </div>
  );
};

export default EmployeeOrderDetail;
